import React from 'react';
import Image from 'next/image';
import { CTAProps, ctaIconsMap } from '../../../lib/composer/schemas/cta';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { getTextColor } from '../../../lib/composer/schemas/theme';
import { textColorClassMap, textGray } from '@osmosis/tailwind-ui/styles/text';
import { bg0, buttonColorClassMap } from '@osmosis/tailwind-ui/styles';

type Props = {
  theme?: string;
  ctas: CTAProps[];
  containerCN?: string;
  buttonCN?: string;
  imageCN?: string;
};

/**
 * A CTA component for composer.
 */
export default function CTAs(props: Props) {
  const {
    ctas = [],
    theme = 'transparent',
    containerCN = '',
    buttonCN = '',
    imageCN = '',
  } = props;
  return (
    <Flex className={containerCN}>
      {ctas.map(
        (
          { caption, url, buttonColor, buttonIcon, image, rounded = true },
          i,
        ) => {
          const color = getTextColor(buttonColor || theme);
          const textColorClass = textColorClassMap[color] || textGray;
          const buttonClasses = buttonColorClassMap[buttonColor || theme];
          const variant = [theme, buttonColor].every((c) => c === 'primaryText')
            ? 'outline'
            : 'default';
          const endIcon =
            buttonIcon?.position === 'end' &&
            ctaIconsMap[buttonIcon?.icon || ''];
          const startIcon =
            buttonIcon?.position === 'start' &&
            ctaIconsMap[buttonIcon?.icon || ''];
          const ctaImage = image;
          return (
            <Flex key={i} className="items-center justify-center">
              <Button
                data-testid="cta-button"
                className={cn(
                  buttonClasses,
                  textColorClass,
                  'mr-2',
                  buttonCN,
                  rounded ? 'rounded-full' : 'rounded-[14px]',
                )}
                as="a"
                href={url}
                rounded={rounded}
                variant={variant}
                endIcon={endIcon ? <Icon icon={endIcon} /> : null}
                startIcon={startIcon ? <Icon icon={startIcon} /> : null}
              >
                {ctaImage && (
                  <Box
                    className={`mr-2 w-[52px] h-[52px] overflow-hidden ${
                      rounded ? 'rounded' : 'rounded-[8px]'
                    } ${bg0} ${imageCN}`}
                    aria-hidden
                  >
                    <Image
                      src={ctaImage}
                      alt={caption}
                      width={52}
                      height={52}
                      className="object-cover"
                    />
                  </Box>
                )}
                {caption}
              </Button>
            </Flex>
          );
        },
      )}
    </Flex>
  );
}
